import type {
  AbsorptionBreakdown,
  Recommendation,
  RT60Rating,
  RT60Result,
} from '@/types/analysis';
import type { AbsorptionSpectrum, SurfaceMaterialName } from '@/types/material';
import type { PaletteObjectId, PlacedObject } from '@/types/object';
import type { RoomDimensions, SurfaceKey, SurfaceMaterials } from '@/types/room';
import { SURFACE_MATERIAL_GROUP } from '@/types/material';
import { getMaterial } from '@data/materials';
import { getPaletteObject } from '@data/paletteObjects';
import { SURFACE_KEYS } from '@data/roomPresets';

/* ─── Geometry ─────────────────────────────────────────────────────── */

/** Room volume in m³. */
export function roomVolume(dims: RoomDimensions): number {
  return dims.L * dims.W * dims.H;
}

/** Area of each of the six surfaces, in m². */
export function surfaceArea(dims: RoomDimensions): Record<SurfaceKey, number> {
  const { L, W, H } = dims;
  return {
    floor: L * W,
    ceiling: L * W,
    'wall-n': L * H, // front/back walls run along L
    'wall-s': L * H,
    'wall-e': W * H, // side walls run along W
    'wall-w': W * H,
  };
}

/** Total interior surface area S = Σ surface areas. */
export function totalSurfaceArea(dims: RoomDimensions): number {
  const a = surfaceArea(dims);
  return a.floor + a.ceiling + a['wall-n'] + a['wall-s'] + a['wall-e'] + a['wall-w'];
}

/**
 * L:W:H ratio analysis. Good studio rooms sit near the Bolt / Louden
 * "preferred ratios" — e.g. 1 : 1.28 : 1.54 — to spread modal frequencies.
 */
export function dimensionRatios(dims: RoomDimensions): {
  ratios: [number, number, number];
  isPreferred: boolean;
  warning?: string;
} {
  const { L, W, H } = dims;
  const ratios: [number, number, number] = [L / H, W / H, 1];
  const lh = L / H;
  const wh = W / H;
  // Cube / near-cube rooms have a notorious amount of modal pile-up.
  const isCubic = Math.abs(lh - 1) < 0.1 && Math.abs(wh - 1) < 0.1;
  const isFlat = lh > 5 || wh > 5;
  const isPreferred = !isCubic && !isFlat && Math.abs(lh - wh) > 0.1;
  let warning: string | undefined;
  if (isCubic) warning = 'Near-cubic — expect modal pile-up and ringing';
  else if (isFlat) warning = 'Very elongated — strong axial modes in one dimension';
  else if (Math.abs(lh - wh) < 0.05) warning = 'L and W too similar — modes collapse';
  return { ratios, isPreferred, ...(warning ? { warning } : {}) };
}

/* ─── Absorption ───────────────────────────────────────────────────── */

/**
 * Total absorption A at each octave band (Sabines). Combines:
 *   Σ surface area × α(band) for each surface/material
 *   + Σ sabins(band) for each placed object.
 */
export function totalAbsorption(
  dims: RoomDimensions,
  materials: SurfaceMaterials,
  objects: readonly PlacedObject[],
): AbsorptionSpectrum {
  const areas = surfaceArea(dims);
  const a: number[] = [0, 0, 0, 0, 0, 0];

  for (const key of SURFACE_KEYS) {
    const matName = materials[key];
    const category = SURFACE_MATERIAL_GROUP[key];
    const mat = getMaterial(category, matName as SurfaceMaterialName);
    if (!mat) continue;
    for (let i = 0; i < 6; i++) a[i] += areas[key] * mat.alpha[i]!;
  }
  for (const placed of objects) {
    const def = getPaletteObject(placed.defId);
    if (!def) continue;
    for (let i = 0; i < 6; i++) a[i] += def.sabins[i]!;
  }

  return a as unknown as AbsorptionSpectrum;
}

/**
 * Detailed breakdown of absorption by source. Used by the Absorption
 * Card to explain "where is the RT60 coming from".
 */
export function absorptionBreakdown(
  dims: RoomDimensions,
  materials: SurfaceMaterials,
  objects: readonly PlacedObject[],
): AbsorptionBreakdown {
  const areas = surfaceArea(dims);
  const bySurface: Record<string, number[]> = {};
  const byObjectKind: Record<string, number[]> = {};

  for (const key of SURFACE_KEYS) {
    const matName = materials[key];
    const category = SURFACE_MATERIAL_GROUP[key];
    const mat = getMaterial(category, matName as SurfaceMaterialName);
    const vec = [0, 0, 0, 0, 0, 0];
    if (mat) {
      for (let i = 0; i < 6; i++) vec[i] = areas[key] * mat.alpha[i]!;
    }
    bySurface[key] = vec;
  }

  for (const placed of objects) {
    const def = getPaletteObject(placed.defId);
    if (!def) continue;
    const vec = byObjectKind[def.id] ?? [0, 0, 0, 0, 0, 0];
    for (let i = 0; i < 6; i++) vec[i] += def.sabins[i]!;
    byObjectKind[def.id] = vec;
  }

  const total = [0, 0, 0, 0, 0, 0];
  for (const vec of Object.values(bySurface)) {
    for (let i = 0; i < 6; i++) total[i]! += vec[i]!;
  }
  for (const vec of Object.values(byObjectKind)) {
    for (let i = 0; i < 6; i++) total[i]! += vec[i]!;
  }

  return {
    bySurface: bySurface as unknown as Record<string, AbsorptionSpectrum>,
    byObjectKind: byObjectKind as unknown as Record<string, AbsorptionSpectrum>,
    total: total as unknown as AbsorptionSpectrum,
  };
}

/* ─── Rating / verdict ─────────────────────────────────────────────── */

/**
 * Map an RT60 (500 Hz) to a semantic bucket. Thresholds mirror the
 * legacy UI; see DESIGN.md §2 "Semantic color mapping".
 */
export function rt60Rating(rt60At500: number): RT60Rating {
  if (rt60At500 < 0.2) return 'fair'; // too dry
  if (rt60At500 < 0.5) return 'excellent';
  if (rt60At500 < 0.8) return 'good';
  return 'poor';
}

/**
 * Short human verdict for the RT60 hero display. Strings match the
 * legacy wording so no UX copy regressions sneak in during the rewrite.
 */
export function rt60Verdict(rt60At500: number): string {
  if (rt60At500 < 0.2) return 'Too dry — sound feels unnatural';
  if (rt60At500 < 0.4) return 'Ideal for recording';
  if (rt60At500 < 0.5) return 'Great for mixing';
  if (rt60At500 < 0.8) return 'Too much reverb — add treatment';
  return 'Very echoey — needs significant treatment';
}

/* ─── Recommendations engine ───────────────────────────────────────── */

/**
 * Produce a short, ranked list of recommendations based on the RT60
 * curve and what the user has already placed. Designed to mirror the
 * legacy "updateRecommendations" output; see index.html:3317.
 */
export function generateRecommendations(
  result: RT60Result,
  objects: readonly PlacedObject[],
): Recommendation[] {
  const recs: Recommendation[] = [];
  const low = result.perBand[0]; // 125 Hz
  const mid = result.hero; // 500 Hz
  const high = result.perBand[5]; // 4 kHz

  const has = (id: PaletteObjectId) => objects.some((o) => o.defId === id);
  const hasAnyMonitor = has('studio-monitors') || has('monitor-stands');
  const hasBassTrap = has('bass-trap');
  const hasFoam = has('foam-panel');
  const hasAbsorbers =
    hasFoam || hasBassTrap || has('curtain') || has('rug');

  if (mid > 1.2) {
    recs.push({
      id: 'too-reverberant',
      severity: 'critical',
      title: 'Room is very echoey',
      detail:
        'Add broadband absorption — foam panels or heavy curtains on the first-reflection walls will tame this quickly.',
      icon: 'volume-x',
      suggestedObjectId: 'foam-panel',
    });
  } else if (mid > 0.8) {
    recs.push({
      id: 'too-reverberant',
      severity: 'warning',
      title: 'Too much reverb',
      detail: 'Try adding a rug, heavy curtain, or a pair of foam panels.',
      icon: 'volume-2',
      suggestedObjectId: 'curtain',
    });
  } else if (mid < 0.15) {
    recs.push({
      id: 'too-dry',
      severity: 'warning',
      title: 'Room sounds over-treated',
      detail:
        'Very low RT60 feels lifeless. Remove some absorption or introduce diffusion.',
      icon: 'waves',
    });
  }

  // Bass control: low > 1.5× mid usually signals modal pile-up.
  if (low > mid * 1.5 && !hasBassTrap) {
    recs.push({
      id: 'low-bass-control',
      severity: 'warning',
      title: 'Bass build-up in the low end',
      detail:
        'Long bass decay at 125 Hz. Corner bass traps are the single highest-leverage fix.',
      icon: 'triangle',
      suggestedObjectId: 'bass-trap',
    });
  }

  // Treble harshness
  if (high > mid * 1.3 && !hasFoam) {
    recs.push({
      id: 'too-bright',
      severity: 'info',
      title: 'High-frequency decay is long',
      detail:
        'Foam panels on the sides or behind the listening position will smooth the top end.',
      icon: 'zap',
      suggestedObjectId: 'foam-panel',
    });
  }

  if (!hasAnyMonitor) {
    recs.push({
      id: 'missing-monitors',
      severity: 'info',
      title: 'No studio monitors placed',
      detail:
        'Place a stereo pair of monitors to preview the listening triangle and sweet spot.',
      icon: 'speaker',
      suggestedObjectId: 'studio-monitors',
    });
  }

  if (mid > 0.5 && !hasAbsorbers) {
    recs.push({
      id: 'missing-treatment',
      severity: 'info',
      title: 'No acoustic treatment placed',
      detail:
        'Even one rug plus a heavy curtain can drop your RT60 by 20–30%.',
      icon: 'layers',
    });
  }

  if (recs.length === 0 && mid >= 0.2 && mid <= 0.5) {
    recs.push({
      id: 'good',
      severity: 'info',
      title: 'Room is in the sweet spot',
      detail:
        'RT60 at 500 Hz is where you want it. Fine-tune with more treatment if bass or treble is off.',
      icon: 'check-circle-2',
    });
  }

  return recs;
}
